import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { downloadCsvFile } from '@percent/partner-dashboard/common/library/utility/utility'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/utility'
import { buildCandidatesReportCsv } from './buildCandidatesReportCsv'
import { UseGenerateReportParams } from './useGenerateReport.types'

export const useGenerateReport = ({
  programId,
  taskTemplates,
  currentState,
  displayGroup,
  questionIds,
  candidateDetails,
  disbursements,
  onGeneratingStart,
  onGeneratingComplete
}: UseGenerateReportParams) => {
  const { t } = useTranslation()
  const { grantsService } = useServices()

  const [generatingError, setGeneratingError] = useState<Error | null>(null)

  const {
    isLoading: isLoadingReport,
    error: reportError,
    refetch: fetchReport
  } = useQuery(
    ['program', programId, 'questions', questionIds, 'candidates-report'],
    grantsService.getReport,
    {
      programId,
      questionIds,
      currentState,
      displayGroup
    },
    { enabled: false }
  )

  const generateReport = async () => {
    onGeneratingStart()

    const report = await fetchReport()

    if (!report.data) {
      setGeneratingError(new Error(t('table.candidatesReport.errors.failedToFetchData')))

      return
    }

    const hasNoData =
      report.data.length === 0 ||
      (candidateDetails.length === 0 &&
        disbursements.length === 0 &&
        !report.data.some(candidate => candidate.submissions.some(submission => submission.answers.length > 0)))

    if (hasNoData) {
      setGeneratingError(new Error(t('table.candidatesReport.errors.noData')))

      return
    }

    const csvBlob = buildCandidatesReportCsv(report.data, {
      taskTemplates,
      questionIds,
      candidateDetails,
      disbursements
    })

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    downloadCsvFile(csvBlob, `grants_candidates_report_${programId}_${timestamp}`)
    onGeneratingComplete()
  }

  return {
    isLoadingReport,
    reportError,
    generatingError,
    resetGeneratingError: () => setGeneratingError(null),
    generateReport
  }
}
